import React, { useState } from 'react';

function formatSyncTime(value) {
  if (!value) return 'No confirmed score sync yet';
  try {
    return `Last synced ${new Date(value).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' })}`;
  } catch (e) {
    return 'Last score sync time unavailable';
  }
}

export default function RoomPanel({ multiplayer, onCreateRoom }) {
  const [joinCode, setJoinCode] = useState('');

  const {
    mode, roomCode, connected,
    leaderTransferRequest, viewerRequestStatus,
    leaderConnected, stateSyncStatus, lastStateSyncedAt,
    error: mpError,
    joinRoom, leaveRoom,
    requestLeader, cancelLeaderRequest, respondToLeaderRequest,
  } = multiplayer;

  // Solo mode — show create/join
  if (mode === 'solo') {
    return (
      <div className="room-panel">
        <div className="section-label">Multiplayer</div>
        <button className="btn btn-gold btn-full" onClick={onCreateRoom}>
          Create Room
        </button>
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          <input
            className="poker-input"
            type="text"
            value={joinCode}
            onChange={e => setJoinCode(e.target.value.toUpperCase())}
            onKeyDown={e => { if (e.key === 'Enter' && joinCode) joinRoom(joinCode); }}
            placeholder="Room code"
            maxLength={5}
            style={{ flex: 1, textTransform: 'uppercase', letterSpacing: 2, textAlign: 'center' }}
          />
          <button
            className="btn btn-gold btn-sm"
            onClick={() => joinCode && joinRoom(joinCode)}
          >
            Join
          </button>
        </div>
        {mpError && <div className="error-bar" style={{ marginTop: 8, fontSize: 12 }}>{mpError}</div>}
      </div>
    );
  }

  // Leader mode
  if (mode === 'leader') {
    return (
      <div className="room-panel">
        <div className="room-code-display">
          <span className="connection-dot" style={{ background: connected ? '#4ade80' : '#fbbf24' }} />
          <span className="section-label" style={{ marginBottom: 0 }}>Room:</span>
          <span className="room-code-text">{roomCode}</span>
          <button
            className="room-copy-btn"
            onClick={() => navigator.clipboard?.writeText(roomCode)}
            title="Copy code"
          >
            Copy
          </button>
        </div>
        <div className="room-role-label leader">Dealer</div>

        {!connected && (
          <div data-testid="dealer-sync-warning" style={{ textAlign: 'center', fontSize: 12, color: '#fbbf24', padding: '6px 0' }}>
            Reconnecting — scoring is paused to protect the shared scores.
          </div>
        )}
        {connected && stateSyncStatus === 'syncing' && (
          <div style={{ textAlign: 'center', fontSize: 11, color: '#c9a84c', padding: '4px 0' }}>
            Saving scores…
          </div>
        )}

        {/* Dealer transfer request notification */}
        {leaderTransferRequest && (
          <div className="transfer-request">
            <div style={{ fontSize: 12, color: '#e2d9c8', marginBottom: 6 }}>
              <strong>{leaderTransferRequest.fromName}</strong> requests dealer control
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-gold btn-sm" style={{ flex: 1 }} onClick={() => respondToLeaderRequest(true)}>
                Accept
              </button>
              <button className="btn btn-muted btn-sm" style={{ flex: 1 }} onClick={() => respondToLeaderRequest(false)}>
                Reject
              </button>
            </div>
          </div>
        )}

        <button className="btn btn-red btn-sm btn-full" onClick={leaveRoom}>
          Leave Room
        </button>
        {mpError && <div className="error-bar" style={{ marginTop: 8, fontSize: 12 }}>{mpError}</div>}
      </div>
    );
  }

  // Viewer mode
  return (
    <div className="room-panel">
      <div className="room-code-display">
        <span className="connection-dot" style={{ background: connected ? '#4ade80' : '#fbbf24' }} />
        <span className="section-label" style={{ marginBottom: 0 }}>Room:</span>
        <span className="room-code-text">{roomCode}</span>
      </div>
      <div className="room-role-label viewer">Viewing</div>

      {!leaderConnected && (
        <div data-testid="viewer-stale-warning" style={{ textAlign: 'center', fontSize: 11, color: '#fbbf24', padding: '6px 0', lineHeight: 1.5 }}>
          <strong>Dealer disconnected — scores may be out of date.</strong>
          <br />{formatSyncTime(lastStateSyncedAt)}
        </div>
      )}

      {viewerRequestStatus === 'none' && (
        <button className="btn btn-muted btn-sm btn-full" disabled={!connected} onClick={() => requestLeader('A viewer')}>
          {!leaderConnected ? 'Review Dealer Takeover' : 'Request Dealer Control'}
        </button>
      )}
      {viewerRequestStatus === 'takeover-warning' && (
        <div className="transfer-request" data-testid="takeover-warning">
          <div style={{ fontSize: 12, color: '#fbbf24', marginBottom: 6, lineHeight: 1.5 }}>
            <strong>Use the last synced scores?</strong><br />
            Any scores still held only on the disconnected dealer's device cannot be recovered here.
          </div>
          <div style={{ fontSize: 11, color: '#a89b86', marginBottom: 8 }}>
            {formatSyncTime(lastStateSyncedAt)}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-muted btn-sm" style={{ flex: 1 }} onClick={cancelLeaderRequest}>
              Cancel
            </button>
            <button className="btn btn-gold btn-sm" style={{ flex: 1 }} onClick={() => requestLeader('A viewer', true)}>
              Take Over Last Synced Scores
            </button>
          </div>
        </div>
      )}
      {viewerRequestStatus === 'pending' && (
        <div style={{ textAlign: 'center', fontSize: 12, color: '#c9a84c' }}>
          Waiting for dealer to respond...
        </div>
      )}
      {viewerRequestStatus === 'rejected' && (
        <div style={{ textAlign: 'center', fontSize: 12, color: '#f87171' }}>
          Request declined
        </div>
      )}

      <button className="btn btn-red btn-sm btn-full" onClick={leaveRoom}>
        Leave Room
      </button>
      {mpError && <div className="error-bar" style={{ marginTop: 8, fontSize: 12 }}>{mpError}</div>}
    </div>
  );
}
